import _ from 'lodash';
import { createItem } from '@/trendData/itemStore.utilities';
import { getUniqueId } from '@/trendData/trend.utilities';
import { ITEM_TYPES, PREVIEW_ID } from '@/trendData/trendData.constants';
import { getCapsuleDuration } from '@/utilities/capsuleDuration.utilities';
import type { CapsuleItem, CapsuleRuntimeItem, CapsuleToAdd, PreviewChartItem } from '@/trendData/trendData.types';

/**
 * Splits a uniqueID into its constituent parts: conditionId and capsuleId
 *
 * @param id - the uniqueId
 * @see .getUniqueId()
 */
export function splitUniqueId(id: string): {
  /** The id of the condition to which the capsule  */
  conditionId: string;
  /** The id of a capsule that is supplied by the backend */
  capsuleId: string;
} {
  const [conditionId, capsuleId] = id.split('_');

  return { conditionId, capsuleId };
}

/**
 * Computes the zones of for a chart item that are selected and therefore colored appropriately.
 *
 * @param {Object} condition - The condition to use for calculating zones
 * @param {String} condition.color - The color of the condition on the trend.
 * @param {Object[]} condition.capsules - The capsules of the condition.
 * @param {String[]} selectedIds - Array of selected capsule ids
 *
 * @return {any[]} Array of two zones per selected capsule, one zone up to the start time with no special color
 * and then a zone with the item's color going up to the end time of the capsule.
 */

export function computeChartZones(condition: any, selectedIds: string[]) {
  return _.transform(condition.capsules, function (zones, capsule: any) {
    if (_.includes(selectedIds, capsule.id)) {
      // to ensure zero length capsules can show as selected we have to slightly alter the end of the zone that is
      // used to show the capsule as selected
      const endTime = capsule.startTime === capsule.endTime ? capsule.endTime + 1 : capsule.endTime;
      zones.push({ value: capsule.startTime });
      zones.push({ value: endTime, color: condition.color });
    }
  });
}

/**
 * Pairs each uncertain capsule with the incoming capsule that replaces it: same condition, sharing
 * a start or end time, under a different id. Uncertain capsules with no replacement are omitted.
 * Shared by the capsule store's sync and the dispatch site, so the flux rename and the runtime
 * re-keying cannot target different items.
 */
export function deriveUncertainCapsuleReplacements(
  existingCapsules: CapsuleItem[],
  incomingCapsules: CapsuleToAdd[],
): Partial<Record<string, CapsuleToAdd>> {
  return _.chain(existingCapsules)
    .filter('isUncertain')
    .transform(
      (map, uncertainCapsule) => {
        map[uncertainCapsule.id] = _.chain(incomingCapsules)
          .filter(['isChildOf', uncertainCapsule.isChildOf])
          .reject(['id', splitUniqueId(uncertainCapsule.id).capsuleId])
          .find(
            (capsule) =>
              uncertainCapsule.startTime === capsule.startTime || uncertainCapsule.endTime === capsule.endTime,
          )
          .thru((capsule) => (capsule ? { ...capsule, id: getUniqueId(capsule.isChildOf, capsule.id) } : capsule))
          .value();
      },
      {} as Record<string, CapsuleToAdd | undefined>,
    )
    .omitBy(_.isUndefined)
    .value() as Partial<Record<string, CapsuleToAdd>>;
}

/** Builds the capsule runtime items from fetched capsules */
export function deriveCapsuleItems(capsules: CapsuleToAdd[]): CapsuleRuntimeItem[] {
  return _.map(capsules, (capsule) => {
    const id = getUniqueId(capsule.isChildOf, capsule.id);

    // createItem seeds a default `selected`, which the runtime items do not carry
    return _.omit(
      createItem(
        id,
        undefined,
        ITEM_TYPES.CAPSULE,
        _.assign(_.omit(capsule, ['id']), {
          isUncertain: capsule.isUncertain,
          cursorKey: capsule.cursorKey,
          duration: getCapsuleDuration(capsule.startTime, capsule.endTime),
        }),
      ),
      ['selected'],
    ) as CapsuleRuntimeItem;
  });
}

/** The payload of TREND_SET_CHART_CAPSULES_PREVIEW and of the runtime preview write */
export interface PreviewChartItemPayload {
  color: string;
  lane: number;
  id?: string;
  /** The compute result: an object whose `capsules` list becomes the preview rows */
  capsules?: { capsules?: unknown[] } | unknown[];
  /** The condition being edited, or the previous preview (an object, or an array of lanes) */
  existingCondition?: { id?: string; lane?: number } | CapsuleItem[];
}

/** Builds the preview chart item from a preview payload */
export function derivePreviewChartItem(payload: PreviewChartItemPayload): PreviewChartItem {
  const color = payload.color;
  const existingCondition = payload.existingCondition;
  const existingId = Array.isArray(existingCondition) ? undefined : existingCondition?.id;
  const existingLane = Array.isArray(existingCondition) ? undefined : existingCondition?.lane;

  if (_.isUndefined(payload.id) || payload.id === existingId) {
    return _.assign(payload.capsules, {
      id: existingId ?? PREVIEW_ID,
      color,
      lane: payload.lane,
      itemType: ITEM_TYPES.CONDITION,
      isPreviewItem: true,
    }) as PreviewChartItem;
  }

  const appendedLane = _.assign(payload.capsules, {
    id: payload.id,
    color,
    lane: (existingLane ?? NaN) + 1 || payload.lane,
    itemType: ITEM_TYPES.CONDITION,
    isPreviewItem: true,
  });

  // The payload shapes are dynamic (the compute result and the previous preview), so the erased
  // cast stays until the preview item gets a real model. Both sides flatten one level.
  return [
    ...(Array.isArray(existingCondition) ? existingCondition : [existingCondition]),
    ...(Array.isArray(appendedLane) ? appendedLane : [appendedLane]),
  ] as PreviewChartItem;
}

/**
 * Merges the flux-owned selection into the capsule dataset items. Shared by every reader that
 * needs selection-aware capsule items so cross-store selectors need not import each other.
 */
export function mergeCapsuleSelection(
  items: CapsuleRuntimeItem[],
  selectedCapsules: Array<{ id: string }>,
): CapsuleItem[] {
  const selectedIds = new Set(_.map(selectedCapsules, 'id'));

  return _.map(items, (item) => ({ ...item, selected: selectedIds.has(item.id) }));
}
